import {makeStyles} from 'tss-react/mui';
import {useTranslation} from 'react-i18next';
import {
  ApiButton,
  Box,
  DatePicker,
  Divider,
  InputLabel,
  PAYLOAD_DATE_FORMAT,
  SectionHeader,
  SelectOptionItem,
  tomorrowDate,
  Typography,
} from 'web-library';
import {RetrieveAuditSettingsResponse} from '../../../api-refactored/audit-settings/useGetRetrieveAuditSettings';
import {IOptionType} from '../../../api/candidate/exam/model';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import NumberOnlyFormItem from '../../../components/form-items/NumberOnlyFormItem';
import {useAuditSettings} from '../logic/useAuditSettings';
import LastUpdatedDateTimeAuditSettings from './LastUpdatedDateTimeAuditSettings';
import dayjs from 'dayjs';

export const repeatData: IOptionType[] = [
  {code: 'MONTHLY', description: 'Monthly'},
  {code: 'YEARLY', description: 'Yearly'},
];

export const reviewReminderData: IOptionType[] = [
  {code: 'DAY', description: 'day'},
  {code: 'WEEK', description: 'week'},
];

const useStyles = makeStyles()((theme) =>
    ({
      reportSettings: {
        marginBottom: theme.spacing(4),
      },
      input: {
        marginTop: theme.spacing(4),
      },
      label: {
        marginRight: theme.spacing(2),
      },
      inputReminder: {
        minWidth: '175px',
        marginRight: theme.spacing(2),
      },
      formElementContainer: {
        width: '40%',
        marginRight: '5%',
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      },
      inputReviewDeadline: {
        width: '70px',
        marginRight: theme.spacing(2),
      },
      row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(6),
        [theme.breakpoints.down('lg')]: {
          paddingTop: theme.spacing(3),
          marginBottom: theme.spacing(5),
        },
        [theme.breakpoints.down('md')]: {
          paddingTop: theme.spacing(2.5),
          marginBottom: theme.spacing(4),
        },
      },
      rowReminder: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: theme.spacing(2),
      },
      reviewReminderDes: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
      },
      buttonUpdate: {
        marginTop: theme.spacing(4),
      },
      divider: {
        marginTop: theme.spacing(4),
      },
      errorText: {
        color: theme.color.state.stateError,
      },
    }));

interface AdminAuditSettingsProps {
  auditSettingsData: RetrieveAuditSettingsResponse | undefined;
  requestType: string;
  isEnableEdit: boolean;
  reportTypesData: SelectOptionItem[] | undefined;
}

const AdminAuditSettings = ({
                              auditSettingsData,
                              requestType,
                              isEnableEdit,
                              reportTypesData,
                            }: AdminAuditSettingsProps) => {
  const {t} = useTranslation();
  const {classes} = useStyles();

  const requestName = reportTypesData?.find((v) => v.code === requestType)?.description;
  const {formHook, handlePutAuditSettings, auditSettingsMutation} = useAuditSettings({
    auditSettingsData: auditSettingsData || undefined,
    requestType,
    requestName,
  });
  const {formState: {errors}, watch, setValue, trigger, register, control, handleSubmit} = formHook;
  const {firstGeneratedDate: generatedDateWatch} = watch();
  // const tomorrow = useMemo(() => {
  //   let tmr = new Date();
  //   const offset = tmr.getTimezoneOffset();
  //   tmr = new Date(tmr.getTime() - offset * 60 * 1000);
  //   tmr.setDate(tmr.getDate() + 1);
  //   return tmr;
  // }, []);

  return (
      <>
        <SectionHeader
            className={classes.reportSettings}
            title={t('admin->>AuditSettings.ReportSettings')}
        />
        <Typography>{t('admin->>AuditSettings.ReportSettingsDes')}</Typography>
        <form onSubmit={handleSubmit(handlePutAuditSettings)}>
          <Box className={classes.formElementContainer}>
            <DatePicker
                id="date-picker-generated-date"
                className={classes.input}
                label={t('admin->>AuditSettings.FirstReportGeneratedDate')}
                value={dayjs(generatedDateWatch) || dayjs(tomorrowDate)}
                onChange={(date) => {
                  setValue('firstGeneratedDate', date || '');
                  trigger('firstGeneratedDate');
                }}
                format={PAYLOAD_DATE_FORMAT}
                error={!!errors.firstGeneratedDate}
                helperText={errors.firstGeneratedDate?.message?.toString()}
                inputVariant="filled"
                autoOk
                isRequired
                minDate={dayjs(tomorrowDate)}
                inputRef={(e) => register('firstGeneratedDate').ref(e)}
                name="firstGeneratedDate"
            />
          </Box>
          <Box className={classes.formElementContainer}>
            <ControlledAutocomplete<IOptionType, false, true, undefined>
                className={classes.input}
                id="input-select-repeat"
                name="repeat"
                control={control}
                label={t('admin->>AuditSettings.Repeat')}
                options={repeatData || []}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                renderOption={(_, option) => option.description}
                error={!!errors?.repeat}
                helperText={errors?.repeat?.message?.toString()}
                isRequired
                disableClearable
                readOnly
            />
          </Box>
          <InputLabel>{t('admin->>AuditSettings.ReviewDeadline')}</InputLabel>
          <Box className={classes.rowReminder}>
            <NumberOnlyFormItem
                className={classes.inputReviewDeadline}
                fullWidth
                variant="outlined"
                id="reviewDeadline"
                name="reviewDeadline"
                error={!!errors?.reviewDeadline}
                placeholder=""
                form={formHook}
                isRequired
                inputProps={{maxLength: 3}}
            />
            <Typography>{t('admin->>AuditSettings.ReviewDeadlineUnit')}</Typography>
          </Box>
          {!!errors?.reviewDeadline && (
              <Typography className={classes.errorText} display="inline" variant="body2">
                {errors?.reviewDeadline?.message}
              </Typography>
          )}
          <Divider className={classes.divider}/>
          <Typography variant="h3">{t('admin->>AuditSettings.ReviewReminder')}</Typography>
          <Typography className={classes.reviewReminderDes}>
            {t('admin->>AuditSettings.ReviewReminderDes')}
          </Typography>
          <InputLabel>{t('admin->>AuditSettings.ReviewReminder')}</InputLabel>
          <Box className={classes.rowReminder}>
            <Typography className={classes.label}>{t('admin->>AuditSettings.Every')}</Typography>
            <ControlledAutocomplete<IOptionType, false, true, undefined>
                className={classes.inputReminder}
                id="input-select-review-reminder"
                name="reminderReviewRepeat"
                control={control}
                options={reviewReminderData || []}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                renderOption={(_, option) => option.description}
                error={!!errors?.reminderReviewRepeat}
                helperText={errors?.reminderReviewRepeat?.message?.toString()}
                isRequired
                disableClearable
                readOnly
            />
            <Typography>{t('admin->>AuditSettings.ReviewReminderUnit')}</Typography>
          </Box>
          <LastUpdatedDateTimeAuditSettings
              reportType={requestType}
              status={auditSettingsMutation.status}
          />
          {isEnableEdit && (
              <ApiButton
                  className={classes.buttonUpdate}
                  status={auditSettingsMutation.status}
                  variant="outlined"
                  id="button-audit-settings-update"
                  type="submit"
              >
                {t('admin->>AuditSettings.Update')}
              </ApiButton>
          )}
        </form>
      </>
  );
};

export default AdminAuditSettings;
